import { Controller, useFormContext } from "react-hook-form";
import Select from "react-select";
import Label from "components/Label/Label";
import Input from "shared/Input/Input";
import { State } from "country-state-city";
import { useForm } from "react-hook-form";
import React, { useMemo, useState, useEffect } from "react";
import { FieldError, OptionalHint } from "./FieldMessages";
import Checkbox from "shared/Checkbox/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Cookies from "js-cookie";
interface DeliveryAddressFormProps {
  control: any;
  errors: any;
  setValue: any;
  clearErrors: any;
  namespace: any;

  isFormValue: any;
}

const DeliveryAddressForm: React.FC<DeliveryAddressFormProps> = ({
  control,
  errors,
  setValue,
  clearErrors,
  namespace,

  isFormValue,
}) => {
  const fid = (name: string) => `${namespace}-${name.replace(/\./g, "-")}`;
  const formatPhone = (value: string) => {
  // remove all non-digits
  const cleaned = value.replace(/\D/g, '');
  // format only if up to 10 digits
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!match) return cleaned;

  let formatted = '';
  if (match[1]) formatted = match[1];
  if (match[2]) formatted += '-' + match[2];
  if (match[3]) formatted += '-' + match[3];
  return formatted;
};
  const formatOptionLabel = ({ label, value }: any, { context }: any) => {
    return context === "value" ? value : label;
  };
  const [isChecked, setIsChecked] = useState(false);
  const AddressValue = JSON.parse(Cookies.get("BillingAddress") as any);
  const isDark = Cookies.get("theme");
  const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const darkForm =
    isDark === "dark" || (isDarkMode === true && isDark === "system");
  const stateOption = useMemo(() => {
    return State.getAllStates()
      .filter((code: any) => code.countryCode === "US")
      .map((state: any) => ({
        label: state.name,
        value: state.isoCode,
      }));
  }, []);
  useEffect(() => {
    if (isChecked && AddressValue) {
      setValue(
        `${namespace}.address1`,
        AddressValue["payment.address1"] || AddressValue.address || ""
      );
      setValue(`${namespace}.address2`, AddressValue["payment.address2"]);
      setValue(
        `${namespace}.country`,
        AddressValue["payment.country"] || "USA" || AddressValue.country
      );
      setValue(
        `${namespace}.zip`,
        AddressValue["payment.zip"] || AddressValue.zip
      );
      setValue(
        `${namespace}.city`,
        AddressValue["payment.city"] || AddressValue.city
      );
      setValue(
        `${namespace}.state`,
        AddressValue["payment.state"] || AddressValue.state || ""
      );
    } else if (isFormValue) {
      return;
    } else {
      setValue(`${namespace}.address1`, "");
      setValue(`${namespace}.address2`, "");
      setValue(`${namespace}.country`, "USA");
      setValue(`${namespace}.zip`, "");
      setValue(`${namespace}.city`, "");
      setValue(`${namespace}.state`, "");
    }
  }, [isChecked, setValue, namespace]);

  return (
    <div className="mt-6 mb-4 space-y-3 sm:space-y-5">
      <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
        Delivery Address
      </h1>
      <FormControlLabel
        control={
          <Checkbox
            name="Same as Billing Address"
            onChange={() => setIsChecked(!isChecked)}
          ></Checkbox>
        }
        label="Same as Billing Address"
      />
      <div className="max-w-lg">
        <Label htmlFor={fid("deliverTo")} className="text-sm">
          <span className="text-red-500" aria-hidden>
            *
          </span>{" "}
          Deliver To (Name)
          <span className="sr-only"> (required)</span>
        </Label>
        <Controller
          name={`${namespace}.deliverTo`}
          control={control}
          rules={{
            required: "Name is required",
          }}
          render={({ field: { ref, ...field } }) => {
            const err = errors[`${namespace}`]?.deliverTo;
            return (
              <Input
                {...field}
                ref={ref}
                id={fid("deliverTo")}
                autoComplete="name"
                placeholder="Name"
                className="mt-1.5 dark:text-gray-100"
                aria-invalid={!!err}
                aria-describedby={err ? `${fid("deliverTo")}-error` : undefined}
                onInput={(e: any) => e.target.value}
              />
            );
          }}
        />
        <FieldError
          id={`${fid("deliverTo")}-error`}
          message={errors[`${namespace}`]?.deliverTo?.message as string}
        />
      </div>
      <div className="max-w-lg">
        <Label htmlFor={fid("contact")} className="text-sm">
          <span className="text-red-500" aria-hidden>
            *
          </span>
          Contact Number
          <span className="sr-only"> (required)</span>
        </Label>
        <Controller
          name={`${namespace}.contact`}
          control={control}
          rules={{
            required: "Phone number is required",
            pattern: { value: /^[0-9]{10}$/, message: "Enter a valid 10-digit phone number" },
          }}
          render={({ field: { onChange, value, ref, ...rest } }) => {
            const err = errors[`${namespace}`]?.contact;
            return (
              <Input
                {...rest}
                ref={ref}
                id={fid("contact")}
                value={formatPhone(value || "")}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, "").slice(0, 10);
                  onChange(raw);
                }}
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Enter 10-digit phone number"
                className="mt-1.5 dark:text-gray-100"
                aria-invalid={!!err}
                aria-describedby={err ? `${fid("contact")}-error` : undefined}
              />
            );
          }}
        />
        <FieldError
          id={`${fid("contact")}-error`}
          message={errors[`${namespace}`]?.contact?.message as string}
        />
      </div>

      <div className="max-w-lg">
        <Label htmlFor={fid("address1")} className="text-sm">
          <span className="text-red-500" aria-hidden>
            *
          </span>
          Address 1
          <span className="sr-only"> (required)</span>
        </Label>
        <Controller
          name={`${namespace}.address1`}
          control={control}
          rules={{
            required: "Street address is required",
          }}
          render={({ field: { ref, ...field } }) => {
            const err = errors[`${namespace}`]?.address1;
            return (
              <Input
                {...field}
                ref={ref}
                id={fid("address1")}
                autoComplete="street-address"
                className="mt-1.5 dark:text-gray-100"
                onInput={(e: any) => e.target.value}
                placeholder="Address 1"
                aria-invalid={!!err}
                aria-describedby={err ? `${fid("address1")}-error` : undefined}
                value={
                  isChecked && AddressValue && !field.value
                    ? AddressValue["payment.address1"] ?? AddressValue.address
                    : field.value
                }
              />
            );
          }}
        />
        <FieldError
          id={`${fid("address1")}-error`}
          message={errors[`${namespace}`]?.address1?.message as string}
        />
      </div>
      <div className="max-w-lg">
        <Label htmlFor={fid("address2")} className="text-sm">
          Address 2
          <OptionalHint />
        </Label>
        <Controller
          name={`${namespace}.address2`}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Input
              {...field}
              ref={ref}
              id={fid("address2")}
              autoComplete="address-line2"
              className="mt-1.5 dark:text-gray-100"
              onInput={(e: any) => e.target.value}
              placeholder="Apartment, suite, etc."
              value={
                isChecked && AddressValue && !field.value
                  ? AddressValue["payment.address2"]
                  : field.value
              }
            />
          )}
        />
      </div>
      <div className="flex flex-col sm:flex-row sm:space-y-0 sm:space-x-3">
        <div className="flex-1">
          <Label htmlFor={fid("city")} className="text-sm">
            <span className="text-red-500" aria-hidden>
              *
            </span>
            City
            <span className="sr-only"> (required)</span>
          </Label>
          <Controller
            name={`${namespace}.city`}
            control={control}
            rules={{
              required: "City is required",
            }}
            render={({ field: { ref, ...field } }) => {
              const err = errors[`${namespace}`]?.city;
              return (
                <Input
                  {...field}
                  ref={ref}
                  id={fid("city")}
                  autoComplete="address-level2"
                  className="mt-1.5 dark:text-gray-100"
                  placeholder="City"
                  onInput={(e: any) => e.target.value}
                  aria-invalid={!!err}
                  aria-describedby={err ? `${fid("city")}-error` : undefined}
                  value={
                    isChecked && AddressValue && !field.value
                      ? AddressValue["payment.city"] ?? AddressValue.city
                      : field.value
                  }
                />
              );
            }}
          />
          <FieldError
            id={`${fid("city")}-error`}
            message={errors[`${namespace}`]?.city?.message as string}
          />
        </div>
        <div className="flex-1">
          <Label htmlFor={`${fid("state")}-input`} className="text-sm">
            <span className="text-red-500" aria-hidden>
              *
            </span>
            State
            <span className="sr-only"> (required)</span>
          </Label>
          <Controller
            name={`${namespace}.state`}
            control={control}
            rules={{
              required: "State is required",
            }}
            render={({ field: { onChange, onBlur, value, ref } }) => {
              const err = errors[`${namespace}`]?.state;
              return (
              <Select
                ref={ref}
                inputId={`${fid("state")}-input`}
                className={`mt-1.5 dark:text-gray-100 input-focus-disable ${err ? "rounded-md ring-2 ring-red-500 ring-offset-0" : ""}`}
                options={stateOption}
                isSearchable
                value={stateOption.find((option) => option.value === value)}
                onChange={(selectedOption) => {
                  onChange(selectedOption ? selectedOption.value : "");
                  clearErrors(`${namespace}.state` as any);
                }}
                onBlur={onBlur}
                aria-invalid={!!err}
                aria-errormessage={err ? `${fid("state")}-error` : undefined}
                formatOptionLabel={formatOptionLabel}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: darkForm ? "rgb(30 41 59)" : "#ffffff",
                    borderColor: darkForm ? "rgb(71 85 105)" : provided.borderColor,
                    height: "48px",
                    overflow: "auto",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: darkForm ? "rgb(30 41 59)" : "#ffffff",
                    border: darkForm ? "1px solid rgb(71 85 105)" : undefined,
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: darkForm
                      ? state.isFocused
                        ? "rgb(51 65 85)"
                        : "rgb(30 41 59)"
                      : state.isFocused
                      ? "#DEEBFF"
                      : "#ffffff",
                    color: darkForm
                      ? "rgb(241 245 249)"
                      : state.isSelected
                      ? "#000000"
                      : "#333333",
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: darkForm ? "rgb(241 245 249)" : "#000000",
                  }),
                }}
              />
              );
            }}
          />
          <FieldError
            id={`${fid("state")}-error`}
            message={errors[`${namespace}`]?.state?.message as string}
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:space-y-0 sm:space-x-3">
        <div className="flex-1">
          <Label htmlFor={fid("country")} className="text-sm">
            <span className="text-red-500" aria-hidden>
              *
            </span>
            Country
            <span className="sr-only"> (required)</span>
          </Label>
          <Controller
            name={`${namespace}.country`}
            control={control}
            rules={{
              required: "Country is required",
            }}
            render={({ field: { ref, ...field } }) => {
              const err = errors[`${namespace}`]?.country;
              return (
                <Input
                  {...field}
                  ref={ref}
                  id={fid("country")}
                  autoComplete="country-name"
                  className="mt-1.5 dark:text-gray-100"
                  onInput={(e: any) => e.target.value}
                  aria-invalid={!!err}
                  aria-describedby={err ? `${fid("country")}-error` : undefined}
                  value={
                    isChecked && AddressValue && !field.value
                      ? AddressValue["payment.country"] ?? AddressValue.country
                      : field.value
                  }
                  placeholder={field.value !== "" ? "USA" : ""}
                />
              );
            }}
          />
          <FieldError
            id={`${fid("country")}-error`}
            message={errors[`${namespace}`]?.country?.message as string}
          />
        </div>
        <div className="flex-1">
          <Label htmlFor={fid("zip")} className="text-sm">
            <span className="text-red-500" aria-hidden>
              *
            </span>
            ZIP code
            <span className="sr-only"> (required)</span>
          </Label>
          <Controller
            name={`${namespace}.zip`}
            control={control}
            rules={{
              required: "ZIP code is required",
            }}
            render={({ field: { ref, ...field } }) => {
              const err = errors[`${namespace}`]?.zip;
              return (
                <Input
                  {...field}
                  ref={ref}
                  id={fid("zip")}
                  autoComplete="postal-code"
                  inputMode="numeric"
                  className="mt-1.5 dark:text-gray-100"
                  onInput={(e: any) => {
                    e.target.value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6);
                  }}
                  aria-invalid={!!err}
                  aria-describedby={err ? `${fid("zip")}-error` : undefined}
                  placeholder="ZIP code"
                  value={
                    isChecked && AddressValue && !field.value
                      ? AddressValue["payment.zip"] ?? AddressValue.zip
                      : field.value
                  }
                />
              );
            }}
          />
          <FieldError
            id={`${fid("zip")}-error`}
            message={errors[`${namespace}`]?.zip?.message as string}
          />
        </div>
      </div>
      <div className="max-w-lg">
        <Label htmlFor={fid("deliveryInstructions")} className="text-sm">
          Delivery instructions
          <OptionalHint />
        </Label>
        <Controller
          name={`${namespace}.deliveryInstructions`}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Input
              {...field}
              ref={ref}
              id={fid("deliveryInstructions")}
              className="mt-1.5 dark:text-gray-100"
              placeholder="Gate code, building entrance, etc."
              maxLength={5000}
              onInput={(e: any) => e.target.value}
            />
          )}
        />
      </div>
    </div>
  );
};
export default DeliveryAddressForm;
